import React from 'react';
import {
  Button,
  Divider,
  Dropdown,
  Empty,
  Flex,
  Input,
  MenuProps,
  Select,
  Space,
  Tooltip,
} from 'antd';
import { DeleteOutlined, InfoCircleOutlined, MoreOutlined } from '@ant-design/icons';
import { colors } from '../../../../../../theme/colors';
import { Question } from '../../../../../../features/frameworks/type';

import Style from './style';
import { Hubspot } from '../../../../../../features/shared/components/Icons/Hubspot';
import { Salesforce } from '../../../../../../features/shared/components/Icons/Salesforce';
const { Container, ItemContent } = Style;

interface ItemProps {
  question: Question;
  isCrmMappingEnabled: boolean;
  provider: string;
  availableProperties: { id: string; label: string }[];
  allProperties: { id: string; label: string }[];
  removeQuestion: (id: string) => void;
  updateQuestion: (question: Question) => void;
}

const Item: React.FC<ItemProps> = ({
  question,
  isCrmMappingEnabled,
  availableProperties,
  provider,
  allProperties,
  removeQuestion,
  updateQuestion,
}) => {
  const [editingContext, setEditingContext] = React.useState(question.context !== null);

  const items: MenuProps['items'] = [
    {
      key: '1',
      label: (
        <a
          onClick={() => {
            if (editingContext) updateQuestion({ ...question, context: null });
            setEditingContext(!editingContext);
          }}
        >
          {editingContext ? 'Remove ' : 'Add/Edit '} Context
        </a>
      ),
    },
    {
      key: '2',
      label: (
        <a
          onClick={() => removeQuestion(question.id)}
          style={{ display: 'flex', alignItems: 'center', color: 'red', gap: '5px' }}
        >
          <DeleteOutlined /> Delete Question
        </a>
      ),
    },
  ];

  return (
    <ItemContent key={question.id}>
      <Flex gap={'10px'}>
        <Input
          placeholder="Add a question or prompt"
          value={question.value}
          style={{ flex: 1 }}
          onChange={v => updateQuestion({ ...question, value: v.target.value })}
        />

        <Dropdown menu={{ items }}>
          <Button style={{ width: '40px' }} icon={<MoreOutlined />} />
        </Dropdown>
      </Flex>

      {editingContext && (
        <div style={{ width: '90%', display: 'flex', gap: '8px', flexDirection: 'column' }}>
          <div
            style={{
              lineHeight: '10px',
              color: colors.gray[500],
              backgroundColor: colors.white,
            }}
          >
            Context
          </div>
          <Input.TextArea
            autoSize={false}
            maxLength={1000}
            style={{ height: '80px' }}
            onChange={v => updateQuestion({ ...question, context: v.target.value })}
            value={question.context || ''}
          />
          <div
            style={{
              fontSize: '10px',
              color: colors.gray[500],
              backgroundColor: colors.white,
            }}
          >
            {1000 - (question.context || '').length}/1000 characters remaining
          </div>
        </div>
      )}
      <div>
        <Space>
          <Space>
            <span>CRM property</span>
            <Tooltip title="Only CRM Company properties of type Text can be selected">
              <InfoCircleOutlined />
            </Tooltip>
          </Space>

          <Select
            disabled={!isCrmMappingEnabled}
            value={question.pushToCrmProperty}
            style={{ width: '300px' }}
            labelRender={label => {
              const value = allProperties.find(d => d.id === label.value);
              return value?.label;
            }}
            optionRender={option => {
              return (
                <Space>
                  {provider === 'hubspot' ? <Hubspot /> : <Salesforce />}
                  <span>{option.label}</span>
                </Space>
              );
            }}
            placeholder="Map to account CRM property"
            allowClear
            showSearch
            onClear={() => updateQuestion({ ...question, pushToCrmProperty: undefined })}
            onChange={value => updateQuestion({ ...question, pushToCrmProperty: value })}
            options={availableProperties.map(({ id, label }) => ({ value: id, label }))}
          />
        </Space>
      </div>

      <Divider style={{ margin: '8px' }} />
    </ItemContent>
  );
};

interface QuestionsProps {
  questions: Question[];
  isCrmMappingEnabled: boolean;
  provider: string;
  availableProperties: { id: string; label: string }[];
  allProperties: { id: string; label: string }[];
  removeQuestion: (id: string) => void;
  updateQuestion: (question: Question) => void;
}

export const Questions: React.FC<QuestionsProps> = ({
  questions,
  availableProperties,
  isCrmMappingEnabled,
  provider,
  allProperties,
  removeQuestion,
  updateQuestion,
}) => {
  return (
    <Container>
      {questions.length === 0 && (
        <Flex align="center" justify="center" style={{ height: '100%' }}>
          <Empty image={Empty.PRESENTED_IMAGE_DEFAULT} description="No questions added yet." />
        </Flex>
      )}
      {questions.map(i => (
        <Item
          allProperties={allProperties}
          provider={provider}
          isCrmMappingEnabled={isCrmMappingEnabled}
          availableProperties={availableProperties}
          key={i.id}
          question={i}
          removeQuestion={removeQuestion}
          updateQuestion={updateQuestion}
        />
      ))}
    </Container>
  );
};
